import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function CSS_AtSupports() {
  return (
    <div className="tutorial-container">
      {/* Hero Section */}
      <div
        style={{
          background: "linear-gradient(135deg, #9333ea, #6d28d9)",
          color: "white",
          padding: "2rem",
          borderRadius: "16px",
          marginBottom: "2rem",
          textAlign: "center",
        }}
      >
        <h1 style={{ fontSize: "2.5rem", fontWeight: "bold" }}>
          🟪 CSS @supports: Feature Queries
        </h1>
        <p style={{ fontSize: "1.2rem", marginTop: "1rem" }}>
          The <code>@supports</code> rule (feature queries) lets you write CSS
          that only runs if the browser supports a specific property or value.
        </p>
        <img
          src="https://developer.mozilla.org/en-US/docs/Web/CSS/@supports/css-supports-feature-queries.png"
          alt="CSS supports illustration"
          style={{
            maxWidth: "500px",
            margin: "1.5rem auto 0",
            borderRadius: "12px",
            boxShadow: "0 6px 20px rgba(0,0,0,0.2)",
          }}
        />
      </div>

      {/* Intro */}
      <p className="tutorial-intro">
        The <b>@supports</b> at-rule (also called <i>feature queries</i>) is a
        way to apply CSS only when a browser supports a certain CSS feature.
        This is especially useful for progressive enhancement, where newer
        browsers get enhanced layouts while older ones still get a fallback.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #e9d5ff, #c4b5fd)",
          padding: "1.2rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#4c1d95" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            Syntax: <code>@supports (property: value)</code> {`{ ... }`}
          </li>
          <li>
            Use logical operators: <code>and</code>, <code>or</code>,{" "}
            <code>not</code>.
          </li>
          <li>
            Great for applying styles only if a feature (like{" "}
            <code>display: grid</code>) is supported.
          </li>
          <li>
            Helps with <b>progressive enhancement</b> instead of relying on
            browser hacks.
          </li>
          <li>
            Nested inside media queries for powerful responsive design
            conditions.
          </li>
        </ul>
      </div>

      {/* Example 1: Basic Feature Query */}
      <h2 className="section-title">1️⃣ Basic Feature Query</h2>
      <CodeCopyBlock
        code={`@supports (display: grid) {
  .container {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
  }
}`}
      />
      <p>
        In this example, the grid layout is only applied if{" "}
        <code>display: grid</code> is supported.
      </p>

      {/* Example 2: Using NOT */}
      <h2 className="section-title">2️⃣ Using NOT</h2>
      <CodeCopyBlock
        code={`@supports not (display: grid) {
  .container {
    float: left;
    width: 100%;
  }
}`}
      />
      <p>
        This code provides a <b>fallback layout</b> for browsers that do not
        support <code>grid</code>.
      </p>

      {/* Example 3: Combining Conditions */}
      <h2 className="section-title">3️⃣ Combining Conditions</h2>
      <CodeCopyBlock
        code={`@supports (display: grid) and (aspect-ratio: 1/1) {
  .box {
    display: grid;
    aspect-ratio: 1/1;
    background: lightgreen;
  }
}`}
      />
      <p>
        Here, the styles only apply if <b>both features</b> are supported.
      </p>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde047)",
          padding: "1.2rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#854d0e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Use <code>@supports</code> for modern layouts like CSS Grid and
            Flexbox.
          </li>
          <li>
            Combine with <code>@media</code> for responsive & feature-specific
            design.
          </li>
          <li>
            Always provide a fallback (inside <code>not()</code>) for
            unsupported browsers.
          </li>
          <li>
            Explore advanced queries like <code>selector()</code> in new CSS
            specs.
          </li>
        </ul>
      </div>
    </div>
  );
}
